<template>
  <div data-tour-id="samples-register-confirmation-panel">
    <div class="input-full">
      <div class="tableContainer">
        <DxDataGrid
          data-tour-id="samples-register-confirmation-grid"
          :data-source="store"
          class="custom-grid custom-samples-confirmation"
          :show-borders="false"
          :show-column-lines="false"
          :allow-column-reordering="true"
          :allow-column-resizing="true"
          :column-auto-width="true"
          :show-column-headers="false"
          :show-colon="false"
          :remote-operations="false"
          :hover-state-enabled="false"
          :show-row-lines="false"
          :row-alternation-enabled="false"
          :no-data-text="$t('noDataTextSummary')"
        >
          <DxScrolling mode="standard" />

          <DxPaging :page-size="10" />

          <DxPager
            :show-page-size-selector="true"
            :allowed-page-sizes="[5, 10, 20]"
            :show-info="true"
            info-text="{2} items"
            :visible="true"
          />

          <DxColumn
            data-field="CodMuestra"
            :caption="$t('agronomySample.summary.sampleCode')"
            :allow-sorting="true"
            :fixed="true"
            fixed-position="left"
            text-align="center"
            :allow-header-filtering="false"
          />
          <DxColumn
            v-if="hasMultipleBudgetCodes"
            data-field="CodPresupuesto"
            :caption="$t('samplesRegister.budget')"
            :allow-sorting="true"
            :fixed="true"
            fixed-position="left"
            :allow-header-filtering="false"
          />
          <DxColumn
            data-field="Muestra"
            :caption="$t('description')"
            :allow-sorting="true"
            :fixed="true"
            fixed-position="left"
            :allow-header-filtering="false"
          />

          <DxColumn
            data-field="AnalisisTipo"
            :caption="$t('samplesRegister.analysisType')"
            :allow-sorting="true"
            :fixed="true"
            alignment="left"
            fixed-position="left"
            :allow-header-filtering="false"
          />

          <DxColumn
            data-field="PrecioAT"
            data-type="number"
            :caption="$t('amount')"
            :allow-sorting="true"
            :fixed="true"
            alignment="right"
            fixed-position="right"
            :allow-header-filtering="false"
            cell-template="priceTemplate"
          />

          <DxColumn
            :buttons="updatedEditButtons"
            :fixed="true"
            fixed-position="right"
            type="buttons"
            width="88"
            name="actionColumn"
          />
          <DxSummary>
            <DxTotalItem
              column="PrecioAT"
              summary-type="sum"
              :display-format="`${$t('SamplesList.totalPrice')}: {0}`"
              show-in-column="PrecioAT"
              :customize-text="formatTotalSummary"
            />
          </DxSummary>

          <template #priceTemplate="{ data }">
            <span>
              {{
                formatAmount(
                  data.data.PrecioAT,
                  getSampleCurrency(data.data)
                )
              }}
            </span>
          </template>
        </DxDataGrid>
      </div>
      <div class="confirm-button-container">
        <f7-button
          class="botones-registrar whiteFontColor"
          data-tour-id="samples-register-save-draft-button"
          fill
          outline
          type="default"
          styling-mode="contained"
          @click="goBack"
        >
          {{ $t('SaveDraft') }}
        </f7-button>
        <f7-button
          class="botones-registrar color-green whiteFontColor"
          data-tour-id="samples-register-confirm-order-button"
          fill
          outline
          type="default"
          styling-mode="contained"
          @click="confirmOrder"
        >
          {{ $t('ConfirmOrder') }}
        </f7-button>
      </div>
    </div>
  </div>
</template>

<script>
import {
  DxDataGrid,
  DxColumn,
  DxScrolling,
  DxPaging,
  DxPager,
  DxSummary,
  DxTotalItem,
} from 'devextreme-vue/data-grid';
import { mapActions, mapGetters, mapState } from 'vuex';
import EventBus from '@/services/event-bus';
import helpers from '@/js/helpers';
import { NOTIFY_TIME_LONG } from '@/js/constants';
import iconPencil from '@/static/icons/ic_pencil.svg';
import iconTrash from '@/static/icons/ic_trash_new.svg';

export default {
  components: {
    DxDataGrid,
    DxColumn,
    DxScrolling,
    DxPaging,
    DxPager,
    DxSummary,
    DxTotalItem,
  },
  props: {
    store: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {};
  },
  computed: {
    invoiceCurrency() {
      const samples = Array.isArray(this.store) ? this.store : [];
      const sampleCurrency = samples
        .map((sample) => this.getSampleCurrency(sample, false))
        .find(Boolean);

      return (
        sampleCurrency ||
        helpers.resolveCurrency({
          conversionX3: this.invoiceDetails?.ConversionX3,
          clientCurrency: this.contactInfo?.currency,
          m: this.invoiceDetails?.M,
          moneda: this.invoiceDetails?.Moneda,
          fallback: 'EUR',
        }) ||
        'EUR'
      );
    },
    hasMultipleBudgetCodes() {
      const budgetCodes = new Set(
        (Array.isArray(this.store) ? this.store : [])
          .map((sample) => sample?.CodPresupuesto)
          .filter(
            (budgetCode) =>
              budgetCode !== null &&
              budgetCode !== undefined &&
              String(budgetCode).trim() !== ''
          )
      );

      return budgetCodes.size > 1;
    },
    updatedEditButtons() {
      return [
        {
          icon: iconPencil,
          cssClass: 'grid-button-icon',
          onClick: this.viewAtc,
        },
        {
          icon: iconTrash,
          cssClass: 'grid-button-icon',
          onClick: this.deleteAtc,
        },
      ];
    },
    ...mapState('samplesRegisterModule', [
      'idInvoice',
      'invoiceSamples',
      'invoiceDetails',
    ]),
    ...mapGetters('contact', ['contactInfo']),
  },
  methods: {
    getSampleCurrency(sample, fallbackToInvoice = true) {
      const currency = helpers.resolveCurrency({
        conversionX3: sample?.ConversionX3,
        clientCurrency: this.contactInfo?.currency,
        m: sample?.M,
        moneda: sample?.Moneda,
        fallback: null,
      });

      if (currency || !fallbackToInvoice) return currency;

      return helpers.resolveCurrency({
        conversionX3: this.invoiceDetails?.ConversionX3,
        clientCurrency: this.contactInfo?.currency,
        m: this.invoiceDetails?.M,
        moneda: this.invoiceDetails?.Moneda,
        fallback: 'EUR',
      });
    },
    formatAmount(amount, currency) {
      if (amount === null || amount === undefined || amount === '') {
        return '';
      }

      const parsedAmount = Number(
        typeof amount === 'string'
          ? amount.replace(',', '.').replace(/[^\d.-]/g, '')
          : amount
      );

      if (Number.isNaN(parsedAmount)) {
        return String(amount);
      }

      return helpers.formatCurrency(parsedAmount, currency, 'es');
    },
    formatTotalSummary(summaryInfo) {
      return this.formatAmount(
        summaryInfo?.value,
        this.invoiceCurrency
      );
    },
    viewAtc(e) {
      this.setDetailedSample({
        ...e.row.data,
        navigationSource: 'samplesRegisterConfirmation',
      });
      this.$f7router.navigate(
        {
          name: 'SamplesDetails',
        },
        {
          animate: true,
          transition: 'f7-fade',
        }
      );
    },
    deleteAtc(e) {
      const dataGrid = e.component;
      dataGrid.deleteRow(e.row.rowIndex);
      this.deleteSample(e.row.data.IdWMuestra);
    },
    addSample() {
      EventBus.$emit('addSample', 0);
    },
    confirmOrder() {
      EventBus.$emit('confirmOrder', 2);
    },
    async deleteCurrentInvoiceIfEmpty() {
      if (!this.idInvoice) return;

      await this.fetchInvoiceSamples(this.idInvoice);
      const hasSamples =
        Array.isArray(this.invoiceSamples) &&
        this.invoiceSamples.length > 0;

      if (!hasSamples) {
        await this.deleteInvoice({
          idInvoice: this.idInvoice,
          deleteSource: 'confirmation_empty_draft',
        });
      }
    },
    async goBack() {
      try {
        await this.deleteCurrentInvoiceIfEmpty();
      } catch (error) {
        this.$notifyDX(
          {
            message: this.$t(
              this.$helpers.getFilteredErrorMessage(
                JSON.parse(error.message).error
              )
            ),
            width: 550,
          },
          'error',
          NOTIFY_TIME_LONG
        );
      } finally {
        this.$f7router.navigate(
          {
            name: 'SamplesList',
          },
          {
            animate: true,
            transition: 'f7-fade',
          }
        );
      }
    },
    ...mapActions('samplesRegisterModule', [
      'deleteSample',
      'deleteInvoice',
      'fetchInvoiceSamples',
      'setDetailedSample',
    ]),
  },
};
</script>
<style lang="scss">
@import './styles/SamplesConfirmationTable.styles.scss';
</style>
